/**
 * The landing page's scroll-linked scenes.
 *
 * A scene is any element carrying `data-scroll="<start> / <end>"`. Each offset
 * is "<edge of the element> <fraction of the screen>", the way the reference
 * site the owner chose writes them: `start 0.5 / end end` means the run begins
 * when the element's top reaches half way down the screen and ends when its
 * bottom reaches the bottom of the screen. The scene's progress through that
 * run, 0 to 1, is written to it as `--p`, and the CSS does the rest.
 *
 * One listener serves every scene, throttled to a frame, and a scene is only
 * measured while it is near the screen. The value goes straight onto the node:
 * a React render per scroll frame would be the whole page's work.
 *
 * Nothing here looks at the reduced-motion setting. The owner decided the
 * page moves the same for everybody, as the reference does: the effects are
 * a few dozen pixels at most and never move the page itself.
 */

const EDGES: Record<string, number> = { start: 0, center: 0.5, end: 1 };

/** "start 0.5" -> [0, 0.5]: the element's edge, then the screen's. */
function parseOffset(text: string): [number, number] {
  const [edge = 'start', screen = 'start'] = text.trim().split(/\s+/);
  return [EDGES[edge] ?? parseFloat(edge), EDGES[screen] ?? parseFloat(screen)];
}

export function parseScene(spec: string): [[number, number], [number, number]] {
  const [from = 'start end', to = 'end start'] = spec.split('/');
  return [parseOffset(from), parseOffset(to)];
}

/** Progress through the run, clamped, for an element `top` px from the screen's top. */
export function sceneProgress(
  top: number,
  height: number,
  screen: number,
  [[edgeA, screenA], [edgeB, screenB]]: [[number, number], [number, number]],
): number {
  const begin = screen * screenA - height * edgeA;
  const end = screen * screenB - height * edgeB;
  if (begin === end) return top <= begin ? 1 : 0;
  return Math.min(1, Math.max(0, (begin - top) / (begin - end)));
}

type Scene = { el: HTMLElement; run: ReturnType<typeof parseScene>; near: boolean; last: string };

/**
 * A card stack holds its cards at one height, the tallest, so a card that
 * slides over another covers it rather than leaving its foot showing. If even
 * that height does not fit the screen under the bar, the stack is switched
 * off and the cards simply follow one another down the page.
 */
function fitStack(stack: HTMLElement, screen: number) {
  const cards = Array.from(stack.children).filter(
    (child): child is HTMLElement => child instanceof HTMLElement,
  );
  stack.style.removeProperty('--stack-h');
  let tallest = 0;
  for (const card of cards) tallest = Math.max(tallest, Math.ceil(card.offsetHeight));
  const top = parseFloat(getComputedStyle(stack).getPropertyValue('--stack-top')) || 96;
  const fits = tallest > 0 && tallest + top + 16 <= screen;
  stack.dataset.stack = fits ? 'on' : 'off';
  if (fits) stack.style.setProperty('--stack-h', `${tallest}px`);
}

/**
 * `drift` elements are handed the page's scroll position as `--scroll`, for a
 * parallax that is not a run. It is written on them alone: on the page's root
 * it made every element on the page restyle on every frame.
 */
export function startScrollScenes(root: HTMLElement, drift: HTMLElement[] = []): () => void {
  const scenes: Scene[] = Array.from(root.querySelectorAll<HTMLElement>('[data-scroll]')).map((el) => ({
    el,
    run: parseScene(el.dataset.scroll ?? ''),
    /*
     * Not near until the observer below says so; it reports every scene once
     * as soon as it starts. Starting them all as near measured every scene on
     * the page in the first frame, a forced layout of the whole page while it
     * was still loading, and wrote a value to scenes five screens down that
     * nobody could see. A scene is now first written when it comes within a
     * screen of view, which is still before it can be seen.
     */
    near: false,
    last: '',
  }));
  const stacks = Array.from(root.querySelectorAll<HTMLElement>('[data-stack]'));

  let frame = 0;
  const measure = () => {
    frame = 0;
    const screen = window.innerHeight;
    // Every box is read before any value is written. Reading a box after a
    // write makes the browser restyle and lay out the page on the spot, once
    // per scene, and that was most of what a scroll frame cost.
    const near = scenes.filter((scene) => scene.near);
    const values = near.map((scene) => {
      const box = scene.el.getBoundingClientRect();
      // Three places is under a fifth of a pixel on the ideas rows' 160px
      // drift; a fourth only restyled the scene again for a change nobody
      // could see.
      return sceneProgress(box.top, box.height, screen, scene.run).toFixed(3);
    });
    const y = String(Math.round(window.scrollY));
    // A drifting piece leaves the screen within a screen or two of the top;
    // past that its value is only work.
    const drifting = window.scrollY < screen * 2;
    near.forEach((scene, i) => {
      const value = values[i]!;
      if (value === scene.last) return;
      scene.last = value;
      scene.el.style.setProperty('--p', value);
    });
    if (drifting) for (const el of drift) el.style.setProperty('--scroll', y);
  };
  const onScroll = () => {
    if (!frame) frame = requestAnimationFrame(measure);
  };
  /*
   * The stack is fitted to the screen once per width, against the smallest
   * height the screen has had at that width. A phone's address bar sliding
   * away mid-scroll fires a resize that only changes the height; refitting on
   * it switched the stack between held and plain on a small phone and dropped
   * every card's height for a frame, and the page jumped under the finger.
   */
  let fitWidth = window.innerWidth;
  let fitScreen = window.innerHeight;
  const refit = () => stacks.forEach((stack) => fitStack(stack, fitScreen));
  const onResize = () => {
    if (window.innerWidth !== fitWidth) {
      fitWidth = window.innerWidth;
      fitScreen = window.innerHeight;
      refit();
    } else if (window.innerHeight < fitScreen) {
      // Only a shrink can make a held stack stop fitting; a grow never refits.
      fitScreen = window.innerHeight;
      if (stacks.some((stack) => stack.dataset.stack === 'on')) refit();
    }
    onScroll();
  };

  // Scenes a screen or more away are skipped; one coming near is measured at once.
  const near = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const scene = scenes.find((s) => s.el === entry.target);
        if (scene) scene.near = entry.isIntersecting;
      }
      onScroll();
    },
    { rootMargin: '100% 0px 100% 0px' },
  );
  scenes.forEach((scene) => near.observe(scene.el));

  refit();
  // Pictures arriving late change the cards' height once more.
  const late = window.setTimeout(refit, 600);
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize, { passive: true });
  measure();

  return () => {
    near.disconnect();
    window.clearTimeout(late);
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', onResize);
    if (frame) cancelAnimationFrame(frame);
  };
}
